import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FramingForm from './FramingForm';
import HypothesesForm from './HypothesesForm';
import ExperimentForm from './ExperimentForm';
import DecisionForm from './DecisionForm';
import IdeasForm from './IdeasForm';
import AuditTrail from './AuditTrail';
import JourneyProgressBreadcrumb from './JourneyProgressBreadcrumb';
import '../FacilitatorJourneyDetail.css';

const API_URL = 'http://localhost:5000';

function FacilitatorJourneyDetail({ journey, token, userRole, onBack, onTeamUpdate }) {
  const [activeTab, setActiveTab] = useState(userRole === 'contributor' ? 'ideas' : 'overview');
  const [teamMembers, setTeamMembers] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [journeyData, setJourneyData] = useState(journey);

  // Add Team Member modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addingMember, setAddingMember] = useState(false);

  // Journey controls state
  const [updatingStage, setUpdatingStage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notesContent, setNotesContent] = useState(journeyData?.facilitator_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Admin edit state
  const [editTitle, setEditTitle] = useState(journeyData?.title || '');
  const [editDescription, setEditDescription] = useState(journeyData?.description || '');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editSaved, setEditSaved] = useState(false);

  const journeyStages = ['intake', 'framing', 'hypotheses', 'experiment', 'decision'];
  const journeyStatuses = ['active', 'paused', 'completed'];

  useEffect(() => {
    fetchTeamMembers();
  }, [journey.id, token]);

  useEffect(() => {
    setNotesContent(journeyData?.facilitator_notes || '');
  }, [journeyData?.facilitator_notes]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch journey details
      const journeyRes = await axios.get(`${API_URL}/api/journeys/${journey.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJourneyData(journeyRes.data.journey);

      // Fetch team members
      const teamRes = await axios.get(`${API_URL}/api/journeys/${journey.id}/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const teamData = teamRes.data.team || [];
      const formattedTeam = teamData.map(member => ({
        id: member.user_id,
        name: member.name,
        email: member.email,
        role: member.journey_role === 'facilitator' ? 'Facilitator' : 'Contributor',
        journey_role: member.journey_role,
      }));
      setTeamMembers(formattedTeam);

      // Fetch ideas
      try {
        const ideasRes = await axios.get(`${API_URL}/api/journeys/${journey.id}/ideas`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIdeas(ideasRes.data.ideas || []);
      } catch (ideasErr) {
        console.error('Fetch ideas error:', ideasErr);
        // Don't fail the whole page if ideas fail to load
        setIdeas([]);
      }

      // Fetch comments
      try {
        const commentsRes = await axios.get(`${API_URL}/api/journeys/${journey.id}/comments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setComments(commentsRes.data.comments || []);
      } catch (commentsErr) {
        console.error('Fetch comments error:', commentsErr);
        // Don't fail the whole page if comments fail to load
        setComments([]);
      }
    } catch (err) {
      console.error('Fetch team members error:', err);
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/journeys/${journey.id}/team/available-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableUsers(res.data.available_users || []);
      setFilteredUsers(res.data.available_users || []);
    } catch (err) {
      console.error('Fetch available users error:', err);
      setError('Failed to load available users');
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.length === 0) {
      setFilteredUsers(availableUsers);
    } else {
      const filtered = availableUsers.filter(user =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
      setFilteredUsers(filtered);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      setAddingMember(true);
      await axios.post(
        `${API_URL}/api/journeys/${journey.id}/team`,
        { user_id: selectedUser.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Reset modal and refresh team
      setShowAddModal(false);
      setSearchQuery('');
      setSelectedUser(null);
      setFilteredUsers([]);
      await fetchTeamMembers();
      if (onTeamUpdate) onTeamUpdate();
    } catch (err) {
      console.error('Add team member error:', err);
      setError(err.response?.data?.message || 'Failed to add team member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleOpenAddModal = async () => {
    setShowAddModal(true);
    await fetchAvailableUsers();
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/journeys/${journey.id}/team/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchTeamMembers();
    } catch (err) {
      console.error('Delete team member error:', err);
      setError('Failed to delete team member');
    }
  };

  const handleUpdateStage = async (newStage) => {
    try {
      setUpdatingStage(true);
      await axios.put(
        `${API_URL}/api/journeys/${journey.id}`,
        { current_stage: newStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh journey data to confirm changes persisted
      await fetchTeamMembers();
      setError('');
    } catch (err) {
      console.error('Update stage error:', err);
      setError('Failed to update journey stage');
    } finally {
      setTimeout(() => setUpdatingStage(false), 500);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    console.log('handleUpdateStatus called with:', newStatus);
    try {
      setUpdatingStatus(true);
      await axios.put(
        `${API_URL}/api/journeys/${journey.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh journey data to confirm changes persisted
      await fetchTeamMembers();
      setError('');
    } catch (err) {
      console.error('Update status error:', err);
      setError('Failed to update journey status');
    } finally {
      setTimeout(() => setUpdatingStatus(false), 500);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await axios.put(
        `${API_URL}/api/journeys/${journey.id}`,
        { facilitator_notes: notesContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJourneyData({ ...journeyData, facilitator_notes: notesContent });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
      setError('');
    } catch (err) {
      console.error('Save notes error:', err);
      setError('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleContinueJourney = async () => {
    try {
      const currentStageIndex = journeyStages.indexOf(journeyData?.current_stage || 'intake');
      const nextStageIndex = Math.min(currentStageIndex + 1, journeyStages.length - 1);
      const nextStage = journeyStages[nextStageIndex];
      await handleUpdateStage(nextStage);
    } catch (err) {
      console.error('Continue journey error:', err);
      setError('Failed to advance to next stage');
    }
  };

  const handleCompleteJourney = async (ideas) => {
    try {
      // Update journey stage to next stage
      const currentStageIndex = journeyStages.indexOf(journeyData?.current_stage || 'intake');
      const nextStageIndex = Math.min(currentStageIndex + 1, journeyStages.length - 1);
      const nextStage = journeyStages[nextStageIndex];

      await axios.patch(
        `${API_URL}/api/journeys/${journey.id}/stage`,
        { stage: nextStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setJourneyData({ ...journeyData, current_stage: nextStage });

      // Refresh all data
      await fetchTeamMembers();
      setError('');
    } catch (err) {
      console.error('Complete journey error:', err);
      setError('Failed to complete journey phase');
    }
  };

  const handleSaveJourneyEdit = async () => {
    try {
      setSavingEdit(true);
      await axios.put(
        `${API_URL}/api/journeys/${journey.id}`,
        {
          title: editTitle,
          description: editDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJourneyData({ ...journeyData, title: editTitle, description: editDescription });
      setEditSaved(true);
      setTimeout(() => setEditSaved(false), 3000);
      setError('');
    } catch (err) {
      console.error('Save edit error:', err);
      setError('Failed to save journey changes');
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading || !journeyData) {
    return (
      <div className="facilitator-journey-detail">
        <div className="loading">Loading journey details...</div>
      </div>
    );
  }

  return (
    <div className="facilitator-journey-detail">
      {/* Header */}
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div>
          <h1>{journeyData?.title || 'Journey'}</h1>
          <p className="subtitle">{journeyData?.description || ''}</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Journey Progress Breadcrumb */}
      <JourneyProgressBreadcrumb currentStage={journeyData?.current_stage} journey={journeyData} />

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          {/* Overview */}
          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              ℹ️ Overview
            </button>
          )}

          {/* Team Management */}
          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              👥 Team Management
            </button>
          )}

          {/* Workflow Stages - Framing */}
          {userRole === 'facilitator' && ['framing', 'hypotheses', 'experiment', 'decision'].includes(journeyData?.current_stage) && (
            <button
              className={`tab ${activeTab === 'framing' ? 'active' : ''}`}
              onClick={() => setActiveTab('framing')}
            >
              🎯 Framing
            </button>
          )}

          {/* Workflow Stages - Ideas */}
          <button
            className={`tab ${activeTab === 'ideas' ? 'active' : ''}`}
            onClick={() => setActiveTab('ideas')}
          >
            💡 Ideas ({ideas.length})
          </button>

          {/* Workflow Stages - Hypotheses */}
          {userRole === 'facilitator' && ['hypotheses', 'experiment', 'decision'].includes(journeyData?.current_stage) && (
            <button
              className={`tab ${activeTab === 'hypotheses' ? 'active' : ''}`}
              onClick={() => setActiveTab('hypotheses')}
            >
              🔬 Hypotheses
            </button>
          )}

          {/* Workflow Stages - Experiment */}
          {userRole === 'facilitator' && ['experiment', 'decision'].includes(journeyData?.current_stage) && (
            <button
              className={`tab ${activeTab === 'experiment' ? 'active' : ''}`}
              onClick={() => setActiveTab('experiment')}
            >
              🧪 Experiment
            </button>
          )}

          {/* Workflow Stages - Decision */}
          {userRole === 'facilitator' && journeyData?.current_stage === 'decision' && (
            <button
              className={`tab ${activeTab === 'decision' ? 'active' : ''}`}
              onClick={() => setActiveTab('decision')}
            >
              ✅ Decision
            </button>
          )}

          {/* Supporting Features */}
          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              📜 Audit Trail
            </button>
          )}

          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              💬 Discussions ({comments.length})
            </button>
          )}

          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'controls' ? 'active' : ''}`}
              onClick={() => setActiveTab('controls')}
            >
              ⚙️ Controls
            </button>
          )}

          {userRole === 'facilitator' && (
            <button
              className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              📝 Notes
            </button>
          )}

          {/* Admin Only */}
          {userRole === 'admin' && (
            <button
              className={`tab ${activeTab === 'edit' ? 'active' : ''}`}
              onClick={() => setActiveTab('edit')}
            >
              ✏️ Edit Journey
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-panel overview-panel">
              <div className="overview-section">
                <h3>Journey Configuration</h3>
                <div className="overview-grid">
                  <div className="overview-item">
                    <label>Title</label>
                    <p>{journeyData?.title}</p>
                  </div>
                  <div className="overview-item">
                    <label>Status</label>
                    <p>
                      <span className={`status-badge ${journeyData?.status?.toLowerCase()}`}>
                        {journeyData?.status?.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <div className="overview-item">
                    <label>Description</label>
                    <p>{journeyData?.description || 'No description provided'}</p>
                  </div>
                  <div className="overview-item">
                    <label>Current Stage</label>
                    <p className="stage-badge">{journeyData?.current_stage || 'Unknown'}</p>
                  </div>
                  <div className="overview-item">
                    <label>Max Participants</label>
                    <p>{journeyData?.max_participants || 'Unlimited'}</p>
                  </div>
                  <div className="overview-item">
                    <label>Team Members</label>
                    <p>{teamMembers.length}</p>
                  </div>
                  <div className="overview-item">
                    <label>Ideas/Submissions</label>
                    <p>{journeyData?.ideas_count || 0}</p>
                  </div>
                  <div className="overview-item">
                    <label>Created</label>
                    <p>{journeyData?.created_at ? new Date(journeyData.created_at).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {/* Continue Journey Button */}
              {userRole === 'facilitator' && journeyData?.current_stage !== 'decision' && (
                <div className="continue-section">
                  <button
                    className="btn btn-primary continue-btn"
                    onClick={handleContinueJourney}
                    disabled={updatingStage}
                  >
                    {updatingStage ? '⏳ Advancing...' : `➜ Continue to ${journeyStages[journeyStages.indexOf(journeyData?.current_stage || 'intake') + 1]?.charAt(0).toUpperCase() + journeyStages[journeyStages.indexOf(journeyData?.current_stage || 'intake') + 1]?.slice(1) || 'Next Stage'}`}
                  </button>
                  <p className="stage-info">Current Stage: <strong>{journeyData?.current_stage?.toUpperCase()}</strong></p>
                </div>
              )}

              {journeyData?.current_stage === 'decision' && (
                <div className="continue-section">
                  <p className="completion-message">✓ Journey completed - all stages finished</p>
                </div>
              )}
            </div>
          )}

          {/* Edit Journey Tab - Admin Only */}
          {activeTab === 'edit' && userRole === 'admin' && (
            <div className="tab-panel edit-panel">
              <div className="edit-section">
                <h3>Edit Journey Configuration</h3>

                <div className="form-group">
                  <label>Journey Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Enter journey title"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter journey description"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label>Current Stage</label>
                  <div className="stage-buttons">
                    {journeyStages.map(stage => (
                      <button
                        key={stage}
                        className={`stage-btn ${journeyData?.current_stage === stage ? 'active' : ''}`}
                        onClick={() => handleUpdateStage(stage)}
                        disabled={updatingStage}
                      >
                        <span className="stage-name">{stage.charAt(0).toUpperCase() + stage.slice(1)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Journey Status</label>
                  <div className="status-buttons">
                    {journeyStatuses.map(status => (
                      <button
                        key={status}
                        className={`status-btn status-${status} ${journeyData?.status === status ? 'active' : ''}`}
                        onClick={() => handleUpdateStatus(status)}
                        disabled={updatingStatus}
                      >
                        {status === 'active' && '▶️ Active'}
                        {status === 'paused' && '⏸️ Paused'}
                        {status === 'completed' && '✓ Completed'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveJourneyEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                {editSaved && <div className="success-message">✓ Journey updated successfully</div>}
              </div>
            </div>
          )}

          {/* Team Management Tab */}
          {activeTab === 'team' && (
            <div className="tab-panel team-panel">
              <div className="team-section">
                <h3>Team Members ({teamMembers.length})</h3>
                {teamMembers.length === 0 ? (
                  <p className="empty">No team members yet</p>
                ) : (
                  <div className="team-list">
                    {teamMembers.map(member => (
                      <div key={member.id} className="team-member">
                        <div className="member-info">
                          <div className="member-name">{member.name}</div>
                          <div className="member-email">{member.email}</div>
                          <div className="member-role">{member.role}</div>
                        </div>
                        <div className="member-actions">
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteMember(member.id)}
                            title="Remove member"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="team-actions">
                <button className="btn btn-primary" onClick={handleOpenAddModal}>
                  + Add Team Member
                </button>
              </div>
            </div>
          )}

          {/* Framing Tab */}
          {activeTab === 'framing' && (
            <FramingForm
              journeyId={journey.id}
              token={token}
              onSuccess={fetchTeamMembers}
              onCancel={() => setActiveTab('overview')}
            />
          )}

          {/* Ideas Tab */}
          {activeTab === 'ideas' && (
            <IdeasForm
              journeyId={journey.id}
              token={token}
              userRole={userRole}
              onSuccess={handleCompleteJourney}
            />
          )}

          {/* Hypotheses Tab */}
          {activeTab === 'hypotheses' && (
            <HypothesesForm
              journeyId={journey.id}
              token={token}
              onSuccess={fetchTeamMembers}
              onCancel={() => setActiveTab('overview')}
            />
          )}

          {/* Experiment Tab */}
          {activeTab === 'experiment' && (
            <ExperimentForm
              journeyId={journey.id}
              token={token}
              onSuccess={fetchTeamMembers}
              onCancel={() => setActiveTab('overview')}
            />
          )}

          {/* Decision Tab */}
          {activeTab === 'decision' && (
            <DecisionForm
              journeyId={journey.id}
              token={token}
              onSuccess={fetchTeamMembers}
              onCancel={() => setActiveTab('overview')}
            />
          )}

          {/* Audit Trail Tab */}
          {activeTab === 'audit' && (
            <AuditTrail
              journeyId={journey.id}
              token={token}
            />
          )}

          {/* Comments Tab */}
          {activeTab === 'comments' && (
            <div className="tab-panel comments-panel">
              <div className="comments-section">
                <h3>Journey Discussions ({comments.length})</h3>
                {comments.length === 0 ? (
                  <p className="empty">No discussions yet</p>
                ) : (
                  <div className="comments-thread">
                    {comments.map(comment => (
                      <div key={comment.id} className="comment-thread">
                        <div className="comment-item main-comment">
                          <div className="comment-header">
                            <div className="comment-author">
                              <strong>{comment.name}</strong>
                              <span className="comment-badge">{comment.comment_type}</span>
                            </div>
                            <span className="comment-date">
                              {new Date(comment.created_at).toLocaleDateString()} {new Date(comment.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="comment-content">{comment.content}</p>
                        </div>

                        {/* Replies */}
                        {comment.replies && comment.replies.length > 0 && (
                          <div className="replies">
                            {comment.replies.map(reply => (
                              <div key={reply.id} className="comment-item reply-comment">
                                <div className="comment-header">
                                  <div className="comment-author">
                                    <strong>{reply.name}</strong>
                                    <span className="comment-badge">{reply.comment_type}</span>
                                  </div>
                                  <span className="comment-date">
                                    {new Date(reply.created_at).toLocaleDateString()} {new Date(reply.created_at).toLocaleTimeString()}
                                  </span>
                                </div>
                                <p className="comment-content">{reply.content}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Controls Tab */}
          {activeTab === 'controls' && (
            <div className="tab-panel controls-panel">
              <div className="controls-section">
                <h3>Journey Controls</h3>

                {/* Journey Stage Control */}
                <div className="control-group">
                  <label>Journey Stage</label>
                  <p className="control-description">Move the journey through its lifecycle stages</p>
                  <div className="stage-buttons">
                    {journeyStages.map(stage => (
                      <button
                        key={stage}
                        className={`stage-btn ${journeyData?.current_stage === stage ? 'active' : ''}`}
                        onClick={() => handleUpdateStage(stage)}
                        disabled={updatingStage}
                      >
                        <span className="stage-name">{stage.charAt(0).toUpperCase() + stage.slice(1)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Journey Status Control */}
                <div className="control-group">
                  <label>Journey Status</label>
                  <p className="control-description">Control whether the journey is active, paused, or completed</p>
                  <div className="status-buttons">
                    {journeyStatuses.map(status => (
                      <button
                        key={status}
                        className={`status-btn status-${status} ${journeyData?.status === status ? 'active' : ''}`}
                        onClick={() => handleUpdateStatus(status)}
                        disabled={updatingStatus}
                      >
                        {status === 'active' && '▶️ Active'}
                        {status === 'paused' && '⏸️ Paused'}
                        {status === 'completed' && '✓ Completed'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Journey Info */}
                <div className="control-group">
                  <label>Journey Information</label>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Current Stage:</span>
                      <span className="info-value">{journeyData?.current_stage || 'Unknown'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Status:</span>
                      <span className="info-value">{journeyData?.status || 'Unknown'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Created:</span>
                      <span className="info-value">
                        {journeyData?.created_at ? new Date(journeyData.created_at).toLocaleDateString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="tab-panel notes-panel">
              <div className="notes-section">
                <h3>Facilitator Notes</h3>
                <p className="notes-description">
                  Keep private notes about the journey progress, observations, and reminders. These are only visible to you.
                </p>

                <textarea
                  className="notes-textarea"
                  value={notesContent}
                  onChange={(e) => setNotesContent(e.target.value)}
                  placeholder="Write your observations, insights, and reminders about this journey..."
                />

                <div className="notes-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                  >
                    {savingNotes ? 'Saving...' : 'Save Notes'}
                  </button>
                  {notesSaved && (
                    <span className="notes-saved-indicator">✓ Notes saved</span>
                  )}
                </div>

                {journeyData?.notes_updated_at && (
                  <p className="notes-meta">
                    Last updated: {new Date(journeyData.notes_updated_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Team Member Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add Team Member</h3>

            <div className="search-section">
              <label>Search Contributors</label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
              />
            </div>

            {filteredUsers.length === 0 ? (
              <p className="empty-results">No contributors available</p>
            ) : (
              <div className="users-list">
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className={`user-option ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => setSelectedUser(user)}
                  >
                    <div className="user-details">
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <input
                      type="radio"
                      checked={selectedUser?.id === user.id}
                      onChange={() => setSelectedUser(user)}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={addingMember}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddMember}
                disabled={!selectedUser || addingMember}
              >
                {addingMember ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FacilitatorJourneyDetail;
